import { createSelector } from 'reselect';
import { createLoadingSelector } from './helpers';
import { getSubState } from '../reducers/subReducerHelpers';
import moment from 'moment-timezone';
import Translation from "../../shared/Translation";
import { DateTimeFormatters } from '@showclix-shared-intl';
import {ISOStringToDate} from "../../helpers/time";

const getEvents = (state) => state.event;
const getLoading = (state) => state.loading;

export const getMainEvent = (state) => {
  if (
    typeof state.event.events === 'object' &&
    state.event.events !== null &&
    !!Object.keys(state.event.events).length
  ) {
    if (state.event.fetchedEventId) {
      return state.event.events[state.event.fetchedEventId];
    }

    return Object.values(state.event.events)[0];
  }
  return null;
};

export const getSelectedEvent = state => {
  if (state.event?.childEvent && typeof state.event.childEvent === 'object' && Object.keys(state.event.childEvent).length > 0) {
    return state.event.childEvent;
  }
  return getMainEvent(state);
};

export const getPriceLevels = createSelector([getMainEvent], (event) => {
  let levels = {};
  if (Array.isArray(event?.price_levels)) {
    event.price_levels.map((pl) => {
      levels[pl.price_level_id] = pl;
    });
  }
  return levels;
});

export const getProducts = createSelector([getMainEvent], (event) => {
  let products = {};
  if (Array.isArray(event?.products)) {
    event.products.map((product) => {
      products[product.product_id] = product;
    });
  }
  return products;
});

export const getBundles = createSelector([getMainEvent], (event) => {
  let bundles = {};
  if (Array.isArray(event?.bundles)) {
    event.bundles.map((bn) => {
      bundles[bn.id] = bn;
    });
  }
  return bundles;
});

export const hasCoupons = createSelector([getSelectedEvent, getMainEvent], (selectedEvent, mainEvent) => {
  return !!selectedEvent?.event?.hasCoupons || !!mainEvent?.event?.hasCoupons;
});

export const get_custom_get_seat_message = createSelector([getMainEvent], (event) => {
  return event?.event?.custom_get_seat_message;
});

export const isSeries = createSelector([getMainEvent], (event) => {
  return !!event?.event?.series;
});

const getPriceLevel = (state) => state.priceLevel;

// Get price levels related to main event in multi listing embed listing
export const getFetchedEventPriceLevels = createSelector(
  [getEvents, getPriceLevel],
  ({ fetchedEventId }, { levels }) => {
    if (!fetchedEventId) return levels;

    const priceLevels = {};

    for (const level in levels) {
      if (Number(levels[level].eventId) === fetchedEventId) {
        priceLevels[level] = levels[level];
      }
    }

    return priceLevels;
  },
);

const getBundleState = (state, reducerKey) => getSubState(state.bundles, reducerKey);

export const getFetchedEventBundles = createSelector([getEvents, getBundleState], ({ fetchedEventId }, { bundles }) => {
  if (!fetchedEventId) return bundles;

  const resBundles = {};

  for (const bundle in bundles) {
    if (Number(bundles[bundle].eventId) === fetchedEventId) {
      resBundles[bundle] = bundles[bundle];
    }
  }
  return resBundles;
});

const getProductsState = (state) => state.products;

export const getFetchedEventProducts = createSelector(
  [getEvents, getProductsState],
  ({ fetchedEventId }, { products }) => {
    if (!fetchedEventId) return products;

    const resProducts = {};

    for (const product in products) {
      if (Number(products[product].eventId) === fetchedEventId) {
        resProducts[product] = products[product];
      }
    }
    return resProducts;
  });

export const getMinPriceLevel = createSelector(
  getMainEvent,
  (event) => {
    if (!event) return undefined;

    if (!event.price_levels.length) return undefined;

    if (event.price_levels.length === 1) return event.price_levels[0].min_price;

    let min = event.price_levels[0];

    for (let i = 1, length = event.price_levels.length; i < length; i++) {
      const priceLevel = event.price_levels[i];

      if (priceLevel.min_price.price < min.min_price.price) {
        min = priceLevel;
      }
    }

    return min.min_price;
  }
);

const getParentEventIdFromTicket = (tickets, eventId) => {
  const ticket = tickets.find((ticket) => ticket.event_id === eventId);

  return ticket?.parent_event_id;
}

const getEventOrUpsellByEventId = (events, upsells, tickets, eventId) => {
  if (events[eventId]) return events[eventId];

  const event = upsells.find((upsell) => {
    if (!upsell.event) {
      return false;
    }

    return upsell.event.event_id === getParentEventIdFromTicket(tickets, eventId);
  });

  return event;
}

export const getEventOrUpsellVenueByEventId = createSelector(
  [
    getEvents,
    state => state.upsells.upsells,
    state => state.tickets.tickets,
    state => state.ui.uiConfig,
    (_, eventId) => eventId
  ],
  (events, upsells, tickets, uiConfig, eventId) => {
    if (uiConfig.hide_venue) {
      return;
    }

    if (uiConfig.listings?.useEventLocation !== 'undefined' && uiConfig.listings?.useEventLocation === false) {
      return uiConfig.listings.location;
    }

    const event = getEventOrUpsellByEventId(events.events, upsells, tickets, eventId);

    if (!event) {
      return;
    }

    const { name, address } = event.venue;

    return [name, address.city, address.region].filter(Boolean).join(', ');
  });

export const isEventDatePast = (date, isUnix = true) => {
  const currentDate = new Date();

  if (!date) return false;

  if (isUnix) {
    const eventEndDate = moment.unix(date);
    if (currentDate > eventEndDate.toDate()) {
      return true;
    }
    return false;
  }

  const eventEndDate = new Date(date);
  return currentDate > eventEndDate;
}

export const eventIsPaused = state => {
  return getMainEvent(state)?.event?.isPaused;
}

const getOnsaleMessage = state => {
  let onsale = state.ui.uiConfig?.listings.hideEventEndMessage ? '' : <Translation>{"checkout__tickets_not_currently_on_sale"}</Translation>;

  const mainEvent = getMainEvent(state);
  const eventOnsaleDate = mainEvent?.event?.onsaleDate ?? '';

  if (mainEvent?.event?.isCanceled) {
      return <Translation>{"checkout__event_has_been_canceled"}</Translation>;
  }

  let timezone = '';
  if(state?.event?.events) {
    const [event] = (Object.values(state?.event?.events));
    timezone = event.event.timezone;
  }
  if(!mainEvent?.event?.isPaused && state.ui.uiConfig?.listings && (state.ui.uiConfig?.listings?.presaleOverride !== undefined)) {
    return state.ui.uiConfig?.listings.presaleOverride;
  }
  if (!mainEvent?.event?.isPaused && state.ui.uiConfig?.listings && state.ui.uiConfig?.listings?.displayOnsaleTime !== undefined) {
      if (state.ui.uiConfig?.listings?.displayOnsaleTime === true && eventOnsaleDate !== null) {
          let onsaleDate = new Date(eventOnsaleDate);
          const { fullDateShort } = DateTimeFormatters;

          if (onsaleDate.toString() == "Invalid Date") {
              onsaleDate = ISOStringToDate(eventOnsaleDate);
          }

          if (onsaleDate >= new Date()) {
              const localeOnsale = fullDateShort(state.intl.locale, onsaleDate);
              onsale = <Translation interpolation={{ localeOnsale, timezone }}>{"checkout__tickets_go_on_sale_with_tz"}</Translation>;
          }
      }
  }
  return onsale
};

export const ticketsGoOnSaleComponentMessage = state => {
  let eventStatusMessage = getOnsaleMessage(state);
  const mainEvent = getMainEvent(state);
  if (
      !mainEvent ||
      !!mainEvent?.event?.isOnsale ||
      (state.couponTag.appliedTaggableInvitationCode || state.reservation.hasInviteCoupon) ||
      (state.ui.uiConfig?.listings?.hideEventEndMessage && !eventStatusMessage)
  ) {
      return false;
  }
  return eventStatusMessage;
}
